import type { TemplateFormField } from '@common/types/templateFormFields';
import { AddOutlined } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import type { Operation } from 'fast-json-patch';
import { produce } from 'immer';
import { useEffect, useMemo, useRef, useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import CurrencyInput from '@/components/CurrencyInput';
import FullScreenLoader from '@/components/FullScreenLoader';
import { InfoSolid, InfoSolidError } from '@/components/Icons/SystemIcons';
import { useOnMount } from '@/hooks/useOnMount';
import {
    addDynamicPrices,
    type DynamicFeesPayload,
    type DynamicPriceType,
    deleteDynamicPriceField,
    patchDynamicPriceField,
} from '@/services/QuoteTemplatesService';
import useQuoteIntakeSetupStore, {
    type InitialSetupStateType,
} from '@/stores/useQuoteIntakeSetupStore';
import { parseCurrency } from '@/utils';
import { errorSnackbar } from '@/utils/snackbar';

const formGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
};

const feeStructureStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '50%',
    gap: '16px',
};

interface PricingModelProps {
    templateFormFields: TemplateFormField[];
}

export default function PricingModel({ templateFormFields }: PricingModelProps) {
    const theme = useTheme();

    //Styles
    const quoteMainContainerCardStyles = {
        display: 'flex',
        gap: '24px',
        boxSizing: 'border-box',
        flexDirection: 'column',
        // (100vh - combined height, borders, paddings and margins of the header, footer and any margin, padding of the content area itself)
        maxHeight: 'calc(100vh - 168px)', // Todo: Move the heights of headers and footers to some sort of variables to better manage this
        minHeight: '400px',
        width: 'calc(100% - 64px)',
        padding: '32px',
        margin: '32px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        backgroundColor: 'neutral.white',
        overflowY: 'auto',
    };

    // State
    const [showTip, setShowTip] = useState(true);
    const [showFeeAddOnTip, setShowFeeAddOnTip] = useState(true);
    const [showDiscountTip, setShowDiscountTip] = useState(true);
    const [
        activeQuote,
        formData,
        setFormData,
        betweenValue,
        showWarning,
        showErrors,
        dynamicFeesErrors,
        fetchQuoteTemplateDataOnEveryStep,
        resetStep3Errors,
        setDynamicPricing,
        setError,
    ] = useQuoteIntakeSetupStore((state) => [
        state.activeQuote,
        state.formData,
        state.setFormData,
        state.formData.step3.betweenValue,
        state.formData.step3.showWarning,
        state.formData.step3.showErrors,
        state.formData.step3.dynamicFeesErrors,
        state.fetchQuoteTemplateDataOnEveryStep,
        state.resetStep3Errors,
        state.setDynamicPricing,
        state.setError,
    ]);
    // const [betweenItems, setBetweenItems] = useState([{ label: 'Between 1' }])
    const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
    const [deleteConfirmAlert, setDeleteConfirmAlert] = useState<{
        isOpen: boolean;
        index: number;
        dynamicFieldId: number | undefined;
    }>({
        isOpen: false,
        index: -1,
        dynamicFieldId: undefined,
    });
    const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
    const [errors, setErrors] = useState<{
        missingValue: boolean | null;
        errorDetails: any;
    }>({
        missingValue: null,
        errorDetails: [],
    });
    const addOnFeeUpdateRef = useRef<number | undefined>(undefined);
    const [isAddDisabled, setIsAddDisabled] = useState<boolean>(false);
    const [variableFeeAddDisabled, setVariableFeeAddDisabled] = useState<boolean>(false);

    const fetchData = async () => {
        if (activeQuote.quoteTemplateId) {
            setIsFullScreenLoading(true);
            try {
                await fetchQuoteTemplateDataOnEveryStep('step3', activeQuote.quoteTemplateId, '1');
            } catch (error) {
                console.error(error);
            } finally {
                setIsFullScreenLoading(false);
            }
        }
    };

    const checkError = () => {
        const hiddenErrorDetails: any = [];
        const optionErrorDetails: any = [];
        const validQuestions = quoteOptionFields.map((option) => option.name);
        formData.step3.dynamicLegalFees.map((field, index) => {
            if (field.question && !validQuestions.includes(field.question)) {
                hiddenErrorDetails.push(
                    {
                        field: 'question',
                        error: true,
                        index: index,
                    },
                    {
                        field: 'answer',
                        error: true,
                        index: index,
                    },
                );
            }
            const validOptions = getOptionsOfSelectedField(
                templateFormFields.find((f) => f.name === field.question),
            )?.map((option) => option.label);
            if (field.answer && !validOptions?.includes(field.answer)) {
                optionErrorDetails.push({
                    field: 'answer',
                    error: true,
                    index: index,
                });
            }
        });

        if (hiddenErrorDetails.length > 0) {
            setErrors({
                missingValue: true,
                errorDetails: hiddenErrorDetails,
            });
            setFormData('step3', {
                ...formData.step3,
                dynamicFeesErrors: {
                    ...dynamicFeesErrors,
                    dynamicHiddenFieldError: true,
                },
            });
        }
        if (optionErrorDetails.length > 0) {
            setErrors({
                missingValue: true,
                errorDetails: optionErrorDetails,
            });
            setFormData('step3', {
                ...formData.step3,
                dynamicFeesErrors: {
                    ...dynamicFeesErrors,
                    optionError: true,
                },
            });
        }
    };

    useOnMount(resetStep3Errors);

    useEffect(() => {
        fetchData();
    }, [activeQuote.quoteTemplateId]);

    useEffect(() => {
        if (showWarning) {
            errorSnackbar(showWarning, true);
        }
    }, [showWarning]);

    useEffect(() => {
        if (formData.step3.dynamicLegalFees) {
            checkError();
        }
        let disableAdd = false;
        formData.step3.dynamicLegalFees?.map((field) => {
            if (
                field.question === '' ||
                field.description === '' ||
                field.answer === '' ||
                field.amount === 0 ||
                !field.amount
            ) {
                disableAdd = true;
                return;
            }
        });
        setIsAddDisabled(disableAdd);
    }, [formData.step3.dynamicLegalFees]);

    useEffect(() => {
        let disableAdd = false;
        formData.step3.betweenValue?.map((field) => {
            if (field.value?.length === 0 || field.legalFee == 0 || !field.legalFee) {
                disableAdd = true;
                return;
            }
            if (field.value?.length > 0) {
                if (
                    !field.value[0] ||
                    field.value[0] == 0 ||
                    !field.value[1] ||
                    field.value[1] == 0
                ) {
                    disableAdd = true;
                    return;
                }
            }
        });
        setVariableFeeAddDisabled(disableAdd);
    }, [formData.step3.betweenValue]);

    // Methods
    const addBetweenItem = () => {
        const newBetweenValue = [...betweenValue];
        // Calculate default values for the new line
        const lastItem = newBetweenValue[newBetweenValue.length - 1];
        const defaultFrom = lastItem ? lastItem.value[1] : 0;
        const defaultTo = '';
        const defaultLegalFee = '';
        const newGreaterThanValue = {
            operatorType: 'greaterThan',
            value: 0,
            legalFee: formData.step3.greaterThanValue?.legalFee,
        };

        newBetweenValue.push({
            operatorType: 'between',
            value: [defaultFrom, defaultTo],
            legalFee: defaultLegalFee,
        });
        setFormData('step3', {
            ...formData.step3,
            betweenValue: newBetweenValue,
            greaterThanValue: newGreaterThanValue,
        });
    };
    const removeBetweenItem = (index: number) => {
        const newBetweenValue = [...betweenValue];
        newBetweenValue.splice(index, 1);
        //After removing an item, update the 'to' value of the next row if there's one
        if (index === newBetweenValue.length) {
            //handling deletion of last between row
            const lastItem = newBetweenValue[index - 1];
            const updatedGreaterThanValue = {
                ...formData.step3.greaterThanValue,
                value: [lastItem.value[1] || ''],
            };
            setFormData('step3', {
                ...formData.step3,
                betweenValue: newBetweenValue,
                greaterThanValue: updatedGreaterThanValue,
                showErrors: {
                    missingValue: null,
                    overlap: null,
                    errorDetails: {
                        lessThan: {},
                        between: [],
                        greaterThan: {},
                    },
                },
            });
        } else {
            //for other between rows
            if (index < newBetweenValue.length) {
                const nextItem = newBetweenValue[index];
                // Update its 'from' value to match the 'to' value of the removed item
                const prevItem = newBetweenValue[index - 1];
                const updatedNextItem = {
                    ...nextItem,
                    value: [prevItem.value[1], nextItem.value[1]],
                };
                newBetweenValue[index] = updatedNextItem; // Update 'from' to previous 'to'
                setFormData('step3', {
                    ...formData.step3,
                    betweenValue: newBetweenValue,
                    showErrors: {
                        missingValue: null,
                        overlap: null,
                        errorDetails: {
                            lessThan: {},
                            between: [],
                            greaterThan: {},
                        },
                    },
                });
            }
        }
    };

    const handleValueChange = (
        key: keyof InitialSetupStateType['formData']['step3'],
        value: any,
    ): void => {
        setFormData('step3', {
            ...formData.step3,
            [key]: value,
        });
    };

    const handleVariableLegalFeeChange = (key: string, value: string): void => {
        const updatedFormData = produce(formData, (draftFormData) => {
            const currentStep3: any = draftFormData.step3;
            const updatedThreshold = currentStep3[key];
            updatedThreshold.legalFee = value;
            currentStep3[key] = updatedThreshold;
        });
        setFormData('step3', updatedFormData.step3);
    };
    const handleVariableLegalFeeChangeForBetween = (
        key: string,
        value: string,
        itemIndex: number,
    ): void => {
        const updatedFormData = produce(formData, (draftFormData) => {
            const currentStep3: any = draftFormData.step3;
            const currentItem = currentStep3[key];

            const updatedThreshold = [...currentItem];
            updatedThreshold[itemIndex].legalFee = value;
        });

        setFormData('step3', updatedFormData.step3);
    };

    const handleVariableValueChange = (key: string, value: string, valueIndex: number): void => {
        const updatedFormData = produce(formData, (draftFormData) => {
            const currentStep3: any = draftFormData.step3;
            const currentThreshold = currentStep3[key].value;

            const updatedThreshold = [...currentThreshold];
            updatedThreshold[valueIndex] = value;

            currentStep3[key].value = updatedThreshold;
            if (key === 'lessThanValue') {
                const newBetweenValue = [...currentStep3.betweenValue];

                // Update the 'from' value of the first "Between" section
                if (newBetweenValue.length > 0) {
                    newBetweenValue[0].value[0] = value; // Update 'from' to match the new 'lessThanValue'
                }

                // Update the form data with the modified 'betweenValue'
                currentStep3.betweenValue = newBetweenValue;
            }
        });

        setFormData('step3', updatedFormData.step3);
    };
    const handleVariableValueChangeForBetween = (
        key: string,
        value: string,
        valueIndex: number,
        itemIndex: number,
    ): void => {
        const updatedFormData = produce(formData, (draftFormData) => {
            const currentStep3: any = draftFormData.step3;
            const currentItem = currentStep3[key][itemIndex];
            const updatedThreshold = [...currentItem.value];
            updatedThreshold[valueIndex] = value;

            currentItem.value = updatedThreshold;
            //Update the 'from' value of the next "Between" row with the new 'to' value
            const nextItemIndex = itemIndex + 1;
            if (nextItemIndex < currentStep3[key].length) {
                const nextItem = currentStep3[key][nextItemIndex];
                nextItem.value[0] = value; // Set the 'from' value of the next item to the updated 'to' value
            }

            //Update the Greater than value with the 'To' value of the last "Between" row
            const lastBetweenIndex = currentStep3.betweenValue.length - 1;
            if (lastBetweenIndex >= 0) {
                const lastBetweenItem = currentStep3.betweenValue[lastBetweenIndex];
                currentStep3.greaterThanValue = {
                    ...currentStep3.greaterThanValue,
                    value: [lastBetweenItem.value[1] || ''],
                };
            }
        });
        setFormData('step3', updatedFormData.step3);
    };

    // Methods for dynamic legal fees
    // Filter questions to select for fee add-ons
    const quoteOptionFields = useMemo(() => {
        return formData.step1.templateFormFields.filter((field) => {
            if (
                field.isVisibleToUser &&
                (field.questionSchema.type === 'radio' || field.questionSchema.type === 'select')
            ) {
                return true;
            }

            //to include conditional questions
            if (field.questionSchema.isConditionalQuestion) {
                const conditionedByField = formData.step1.templateFormFields.find(
                    (f) => f.name === field.questionSchema.conditionedBy,
                );

                //If the field it is conditioned by is visible to the user, include this field
                if (
                    conditionedByField?.isVisibleToUser &&
                    (field.questionSchema.type === 'radio' ||
                        field.questionSchema.type === 'select')
                ) {
                    return true;
                }
            }

            return false;
        });
    }, [formData.step1.templateFormFields]);

    const getOptionsOfSelectedField = (field: TemplateFormField | undefined) => {
        if (!field) {
            return [] as { label: string; value: string; key: string }[];
        }

        if (field.questionSchema.type === 'radio') {
            return field.questionSchema.options?.map((op, index) => ({
                label: op.optionLabel,
                value: op.optionValue,
                key: `${field.name}-${index}`,
            }));
        }

        if (field.questionSchema.type === 'select') {
            return field.questionSchema.selectOptions?.map((sOp, index) => ({
                label: sOp.selectItemLabel,
                value: sOp.selectItemValue,
                key: `${field.name}-${index}`,
            }));
        }

        return [] as { label: string; value: string; key: string }[];
    };

    const removeDynamicLegalFee = async (index: number, dynamicFieldId?: number) => {
        if (isConfirmDisabled) return;

        setIsConfirmDisabled(true);

        if (dynamicFieldId) {
            try {
                await deleteDynamicPriceField(dynamicFieldId);
                const filteredDynamicFields = formData.step3.dynamicLegalFees.filter((f, i) => {
                    if (dynamicFieldId) {
                        return f.quoteTemplateDynamicPriceId !== dynamicFieldId;
                    }

                    return i !== index;
                });
                // Filter errors to remove any related to the deleted field
                const filteredErrors = errors.errorDetails.filter((error) => error.index !== index);

                //Set the state with filtered fields
                setFormData('step3', {
                    ...formData.step3,
                    dynamicLegalFees: filteredDynamicFields,
                    dynamicFeesErrors: {
                        missingValue: null,
                        errorDetails: [],
                    },
                });
                setErrors({
                    missingValue: filteredErrors.length > 0,
                    errorDetails: filteredErrors,
                });
                setDeleteConfirmAlert({
                    isOpen: false,
                    index: -1,
                    dynamicFieldId: undefined,
                });
            } catch (error: any) {
                console.error(error);
                setError(error.response?.data || 'Failed to delete the field');

                const timeout = window.setTimeout(() => {
                    setError(null);
                    window.clearTimeout(timeout);
                }, 5000);
            }
        }
        setIsConfirmDisabled(false);
    };

    const updateDynamicLegalFeeAddOn = async (
        payload: Operation[],
        quoteTemplateDynamicPriceId: number,
    ) => {
        try {
            await patchDynamicPriceField(quoteTemplateDynamicPriceId, payload);
        } catch (error: any) {
            setError(error.response?.data || 'Failed to update the field');

            const timeout = window.setTimeout(() => {
                setError(null);
                window.clearTimeout(timeout);
            }, 5000);
        }
    };

    const handleDynamicLegalValueChange = (
        value: any,
        field: keyof DynamicPriceType,
        index: number,
        quoteTemplateDynamicPriceId?: number,
    ) => {
        if (quoteTemplateDynamicPriceId) {
            const payload: Operation[] = [
                {
                    op: 'replace',
                    path: `/${field}`,
                    value: value,
                },
            ];

            window.clearTimeout(addOnFeeUpdateRef.current);

            addOnFeeUpdateRef.current = window.setTimeout(() => {
                updateDynamicLegalFeeAddOn(payload, quoteTemplateDynamicPriceId);
            }, 200);
        }

        setDynamicPricing('LegalFee', field, index, value);
    };

    const handleAddNewDynamicLegalFee = async () => {
        const tempDynamicPricePayload: DynamicFeesPayload = {
            dynamicPriceCategory: 'LegalFee',
            question: '',
            answer: '',
            description: '',
            amount: 0,
        };

        try {
            if (activeQuote.quoteTemplateId) {
                const dynamicFieldPostResp = await addDynamicPrices(activeQuote.quoteTemplateId, [
                    tempDynamicPricePayload,
                ]);
                handleValueChange('dynamicLegalFees', [
                    ...formData.step3.dynamicLegalFees,
                    ...dynamicFieldPostResp.data,
                ]);
            }
        } catch (error: any) {
            console.error(error);
            setError(error.response?.data || 'Failed to add the field');

            const timeout = window.setTimeout(() => {
                setError(null);
                window.clearTimeout(timeout);
            }, 5000);
        }
    };

    const hasError = (field, index, errorList) =>
        errorList.some((err) => err?.field === field && err?.index === index && err?.error);

    return (
        <Box sx={quoteMainContainerCardStyles}>
            {isFullScreenLoading && <FullScreenLoader />}
            <div>
                <Typography sx={{ marginBottom: '8px' }} variant="h1" component="h1">
                    Fee Details
                </Typography>
                <Typography variant="body1" component="p">
                    Please provide information about your fee.
                </Typography>
            </div>
            <Box sx={formGroupStyles}>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={feeStructureStyles}>
                        <Typography variant="body1" component="p" fontWeight={'500'}>
                            Your fee structure
                        </Typography>
                        <FormControl sx={{ marginTop: '-9px' }}>
                            <RadioGroup
                                row
                                aria-labelledby="demo-row-radio-buttons-group-label"
                                name="row-radio-buttons-group"
                                value={formData.step3.pricingModel || ''}
                                onChange={(ev) => {
                                    handleValueChange('pricingModel', ev.target.value);
                                    setShowDiscountTip(true);
                                }}
                            >
                                <FormControlLabel
                                    value="Flat Fee"
                                    control={<Radio />}
                                    label="Flat Fee"
                                />
                                <FormControlLabel
                                    value="Variable pricing"
                                    control={<Radio />}
                                    label="Variable Fee"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                    <Box
                        sx={{
                            borderLeft: '1px solid',
                            borderColor: 'grey.300',
                            height: '100%',
                            margin: '0 16px',
                        }}
                    />
                    <Box sx={{ ...feeStructureStyles, marginLeft: '25px' }}>
                        <Typography variant="body1" component="p" fontWeight={'500'}>
                            Set Discount
                        </Typography>
                        {showDiscountTip && (
                            <Alert
                                icon={<InfoSolid />}
                                severity="info"
                                onClose={() => {
                                    setShowDiscountTip(false);
                                }}
                            >
                                <AlertTitle>Discounts Provided</AlertTitle>
                                Use this field if you know in advance you will give a discount which
                                offsets the Legal Fee by the amount specified.
                            </Alert>
                        )}
                        <Typography variant="body2" component="p">
                            Discount
                        </Typography>
                        <CurrencyInput
                            sx={{ width: '360px' }}
                            allowDecimal
                            value={formData.step3.legalFeeDiscount || 0}
                            onChange={(value) => {
                                handleValueChange('legalFeeDiscount', value);
                            }}
                        ></CurrencyInput>
                    </Box>
                </Box>
                {formData.step3.pricingModel === 'Flat Fee' && (
                    <>
                        <Divider></Divider>
                        <Typography variant="body1" component="p" fontWeight={'500'}>
                            Set legal fee (pre-tax)
                        </Typography>
                        <CurrencyInput
                            sx={{ width: '360px' }}
                            allowDecimal
                            value={formData.step3.flatFeeValue || 0}
                            onChange={(value) => {
                                handleValueChange('flatFeeValue', value);
                            }}
                            showErrorAdornment={true}
                        ></CurrencyInput>
                    </>
                )}
                {formData.step3.pricingModel === 'Variable pricing' && (
                    <>
                        <Divider></Divider>
                        <Typography variant="body1" component="p" fontWeight={'500'}>
                            Set legal fees for each transaction fee ranges:
                        </Typography>
                        {showTip && (
                            <Alert
                                icon={<InfoSolid />}
                                severity="info"
                                onClose={() => {
                                    setShowTip(false);
                                }}
                            >
                                <AlertTitle>Legal fee configuration</AlertTitle>
                                Specify the minimum and maximum transaction fee range, and set the
                                corresponding legal fee for each range. Simply enter the values in
                                the designated fields to define the fee structure for your services.
                            </Alert>
                        )}
                        {(showErrors.overlap || showErrors.missingValue) && (
                            <Alert icon={<InfoSolidError />} severity="error">
                                <AlertTitle>Errors</AlertTitle>
                                <ul>
                                    {showErrors.missingValue && (
                                        <li>Please ensure no values are missing</li>
                                    )}
                                    {showErrors.overlap && (
                                        <li>
                                            Please ensure that the minimum value is not greater than
                                            or equal to the maximum value.
                                        </li>
                                    )}
                                </ul>
                            </Alert>
                        )}
                        {showWarning && (
                            <Alert icon={<InfoSolidError />} severity="error">
                                <AlertTitle>Errors</AlertTitle>
                                <ul>
                                    <li>
                                        Please ensure there is no overlapping or duplicate values
                                    </li>
                                </ul>
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={6} lg={2}>
                                    <Typography variant="body2" component="p">
                                        Less than
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <CurrencyInput
                                        label="Amount"
                                        fullWidth
                                        value={parseCurrency(
                                            `${formData.step3?.lessThanValue?.value[0]}`,
                                        )}
                                        onChange={(value) => {
                                            handleVariableValueChange(
                                                'lessThanValue',
                                                value?.toString(),
                                                0,
                                            );
                                            resetStep3Errors();
                                        }}
                                        showErrorAdornment={true}
                                    ></CurrencyInput>
                                </Grid>
                                <Grid item xs={6} lg={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Divider
                                            sx={{
                                                marginLeft: '0px',
                                                marginRight: '16px',
                                                height: '24px',
                                            }}
                                            orientation="vertical"
                                        />
                                        <CurrencyInput
                                            value={parseCurrency(
                                                `${formData.step3.lessThanValue?.legalFee}`,
                                            )}
                                            onChange={(value) => {
                                                handleVariableLegalFeeChange(
                                                    'lessThanValue',
                                                    value?.toString(),
                                                );
                                                resetStep3Errors();
                                            }}
                                            error={
                                                showErrors.errorDetails.lessThan?.field ===
                                                    'legalFee' &&
                                                showErrors.errorDetails.lessThan.error
                                            }
                                            InputProps={{
                                                endAdornment: showErrors.errorIndex ? (
                                                    <InputAdornment position="end">
                                                        <InfoSolidError />
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                            label="Legal fee (pre-tax)"
                                            showErrorAdornment={true}
                                            allowDecimal={true}
                                        ></CurrencyInput>
                                    </Box>
                                </Grid>
                            </Grid>
                            {formData.step3.betweenValue.map((item, index) => {
                                return (
                                    <Grid
                                        key={item?.label}
                                        container
                                        alignItems="center"
                                        spacing={2}
                                    >
                                        <Grid item xs={6} lg={2}>
                                            <Typography variant="body2" component="p">
                                                {item.operatorType.charAt(0).toUpperCase() +
                                                    item.operatorType.slice(1) +
                                                    ` ${index + 1}`}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} lg={2}>
                                            <CurrencyInput
                                                value={parseCurrency(`${item.value[0]}`)}
                                                onChange={(value) => {
                                                    handleVariableValueChangeForBetween(
                                                        'betweenValue',
                                                        value?.toString(),
                                                        0,
                                                        index,
                                                    );
                                                    resetStep3Errors();
                                                }}
                                                error={showErrors.errorDetails.between.some(
                                                    (err) =>
                                                        err?.field?.includes('From') &&
                                                        err?.index === index &&
                                                        err?.error,
                                                )}
                                                label="From"
                                                fullWidth
                                                disabled
                                                showErrorAdornment={true}
                                            ></CurrencyInput>
                                        </Grid>
                                        <Grid item xs={6} lg={2}>
                                            <CurrencyInput
                                                value={parseCurrency(`${item?.value[1]}`)}
                                                onChange={(value) => {
                                                    handleVariableValueChangeForBetween(
                                                        'betweenValue',
                                                        value?.toString(),
                                                        1,
                                                        index,
                                                    );
                                                    resetStep3Errors();
                                                }}
                                                error={showErrors.errorDetails.between.some(
                                                    (err) =>
                                                        err?.field?.includes('To') &&
                                                        err?.index === index &&
                                                        err?.error,
                                                )}
                                                label="To"
                                                fullWidth
                                                showErrorAdornment={true}
                                            ></CurrencyInput>
                                        </Grid>
                                        <Grid item xs={6} lg={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Divider
                                                    sx={{
                                                        marginLeft: '0px',
                                                        marginRight: '16px',
                                                        height: '24px',
                                                    }}
                                                    orientation="vertical"
                                                />
                                                <CurrencyInput
                                                    value={parseCurrency(`${item.legalFee}`)}
                                                    onChange={(value) => {
                                                        handleVariableLegalFeeChangeForBetween(
                                                            'betweenValue',
                                                            value?.toString(),
                                                            index,
                                                        );
                                                        resetStep3Errors();
                                                    }}
                                                    error={showErrors.errorDetails.between.some(
                                                        (err) =>
                                                            err?.field?.includes('legalFee') &&
                                                            err?.index === index &&
                                                            err?.error,
                                                    )}
                                                    label="Legal fee (pre-tax)"
                                                    showErrorAdornment={true}
                                                    allowDecimal={true}
                                                ></CurrencyInput>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} lg={2}>
                                            <IconButton
                                                onClick={() => {
                                                    removeBetweenItem(index);
                                                }}
                                                disabled={index === 0}
                                                color={theme.palette.neutral.N_500}
                                            >
                                                <RemoveCircleOutlineIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => {
                                                    addBetweenItem();
                                                }}
                                                color={theme.palette.neutral.N_500}
                                                disabled={variableFeeAddDisabled}
                                            >
                                                <AddCircleOutlineIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                );
                            })}
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={6} lg={2}>
                                    <Typography variant="body2" component="p">
                                        Greater than or equal to
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <CurrencyInput
                                        value={parseCurrency(
                                            `${formData.step3.greaterThanValue?.value[0]}`,
                                        )}
                                        onChange={(value) => {
                                            handleVariableValueChange(
                                                'greaterThanValue',
                                                value?.toString(),
                                                0,
                                            );
                                            resetStep3Errors();
                                        }}
                                        error={
                                            showErrors.errorDetails.greaterThan?.field ===
                                                'amount' &&
                                            showErrors.errorDetails.greaterThan?.error
                                        }
                                        fullWidth
                                        disabled
                                        label="Amount"
                                        showErrorAdornment={true}
                                    ></CurrencyInput>
                                </Grid>
                                <Grid item xs={6} lg={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Divider
                                            sx={{
                                                marginLeft: '0px',
                                                marginRight: '16px',
                                                height: '24px',
                                            }}
                                            orientation="vertical"
                                        />
                                        <CurrencyInput
                                            value={parseCurrency(
                                                `${formData.step3.greaterThanValue?.legalFee}`,
                                            )}
                                            onChange={(value) => {
                                                handleVariableLegalFeeChange(
                                                    'greaterThanValue',
                                                    value?.toString(),
                                                );
                                                resetStep3Errors();
                                            }}
                                            error={
                                                showErrors.errorDetails.greaterThan?.field ===
                                                    'legalFee' &&
                                                showErrors.errorDetails.greaterThan.error
                                            }
                                            label="Legal fee (pre-tax)"
                                            showErrorAdornment={true}
                                            allowDecimal={true}
                                        ></CurrencyInput>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        <Divider></Divider>

                        {/* --------------------- Fee Add Ons ---------------------*/}
                        <Typography variant="body1" component="p" fontWeight={'500'}>
                            Fee add-ons
                        </Typography>
                        {showFeeAddOnTip && (
                            <Alert
                                icon={<InfoSolid />}
                                severity="info"
                                onClose={() => {
                                    setShowFeeAddOnTip(false);
                                }}
                            >
                                <AlertTitle>Fee add-ons configuration</AlertTitle>
                                Customize your quote fee by setting additional fees based on answers
                                to the quote form. Just select the question and desired option and
                                set the custom fee for it. Fees are applicable only for questions
                                with multiple options.
                            </Alert>
                        )}
                        {(dynamicFeesErrors.missingValue ||
                            dynamicFeesErrors.duplicateValue ||
                            dynamicFeesErrors.optionError ||
                            dynamicFeesErrors.dynamicHiddenFieldError) && (
                            <Alert icon={<InfoSolidError />} severity="error">
                                <AlertTitle>Error(s)</AlertTitle>
                                <ul style={{ listStyleType: 'disc', paddingLeft: '25px' }}>
                                    {dynamicFeesErrors.missingValue && (
                                        <li>Please ensure no values are missing</li>
                                    )}
                                    {dynamicFeesErrors.duplicateValue && (
                                        <li>
                                            Please ensure there are no duplicate rows for questions
                                            and their options.
                                        </li>
                                    )}
                                    {dynamicFeesErrors.optionError && (
                                        <li>
                                            The selected option for this question has been edited or
                                            removed from the quote form. Please select an option or
                                            remove the row to proceed.
                                        </li>
                                    )}
                                    {dynamicFeesErrors.dynamicHiddenFieldError && (
                                        <li>
                                            This field has been removed or hidden from the quote
                                            form. If hidden, please unhide it in the quote form or
                                            delete it from the Fee Add-On section to proceed.
                                        </li>
                                    )}
                                </ul>
                            </Alert>
                        )}
                        {formData.step3.dynamicLegalFees.map((field, index) => (
                            <Box
                                key={field.quoteTemplateDynamicPriceId}
                                sx={{
                                    display: 'flex',
                                    gap: '16px',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <Autocomplete
                                    disablePortal
                                    value={templateFormFields
                                        .map((f) => ({
                                            label: f.title,
                                            value: f.name,
                                            key: f.name,
                                        }))
                                        .find((f) => f.value === field.question)}
                                    options={quoteOptionFields.map((f) => ({
                                        label: f.title,
                                        value: f.name,
                                        key: f.name,
                                    }))}
                                    fullWidth
                                    onChange={(_ev, value) => {
                                        handleDynamicLegalValueChange(
                                            value?.value,
                                            'question',
                                            index,
                                            field.quoteTemplateDynamicPriceId,
                                        );
                                        resetStep3Errors();
                                        setErrors({
                                            missingValue: null,
                                            errorDetails: [],
                                        });
                                    }}
                                    popupIcon={
                                        hasError(
                                            'question',
                                            index,
                                            dynamicFeesErrors.errorDetails,
                                        ) || hasError('question', index, errors.errorDetails) ? (
                                            <InfoSolidError />
                                        ) : (
                                            <ArrowDropDownIcon />
                                        )
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Question"
                                            error={
                                                hasError(
                                                    'question',
                                                    index,
                                                    dynamicFeesErrors.errorDetails,
                                                ) ||
                                                hasError('question', index, errors.errorDetails)
                                            }
                                        />
                                    )}
                                />
                                <FormControl fullWidth>
                                    <InputLabel
                                        id={`dynamic-fee-option-label-${field.quoteTemplateDynamicPriceId}`}
                                    >
                                        Option
                                    </InputLabel>
                                    <Select
                                        id={`dynamic-fee-option-select-${field.quoteTemplateDynamicPriceId}`}
                                        labelId={`dynamic-fee-option-label-${field.quoteTemplateDynamicPriceId}`}
                                        label="Option"
                                        value={field.answer}
                                        onChange={(event) => {
                                            handleDynamicLegalValueChange(
                                                event.target.value,
                                                'answer',
                                                index,
                                                field.quoteTemplateDynamicPriceId,
                                            );
                                            resetStep3Errors();
                                            setErrors({
                                                missingValue: null,
                                                errorDetails: [],
                                            });
                                        }}
                                        IconComponent={() => (
                                            <InputAdornment position="end" sx={{ margin: '8px' }}>
                                                {hasError(
                                                    'answer',
                                                    index,
                                                    dynamicFeesErrors.errorDetails,
                                                ) ||
                                                hasError('answer', index, errors.errorDetails) ? (
                                                    <InfoSolidError />
                                                ) : (
                                                    <ArrowDropDownIcon />
                                                )}
                                            </InputAdornment>
                                        )}
                                        error={
                                            hasError(
                                                'answer',
                                                index,
                                                dynamicFeesErrors.errorDetails,
                                            ) || hasError('answer', index, errors.errorDetails)
                                        }
                                    >
                                        {getOptionsOfSelectedField(
                                            templateFormFields.find(
                                                (f) => f.name === field.question,
                                            ),
                                        )?.map((option) => {
                                            return (
                                                <MenuItem value={option.value} key={option.key}>
                                                    {option.label}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                                <Divider orientation="vertical" />
                                <TextField
                                    fullWidth
                                    value={field.description}
                                    label={'Fee description'}
                                    onChange={(event) => {
                                        handleDynamicLegalValueChange(
                                            event.target.value,
                                            'description',
                                            index,
                                            field.quoteTemplateDynamicPriceId,
                                        );
                                        resetStep3Errors();
                                        setErrors({
                                            missingValue: null,
                                            errorDetails: [],
                                        });
                                    }}
                                    error={hasError(
                                        'description',
                                        index,
                                        dynamicFeesErrors.errorDetails,
                                    )}
                                    InputProps={{
                                        endAdornment: hasError(
                                            'description',
                                            index,
                                            dynamicFeesErrors.errorDetails,
                                        ) ? (
                                            <InputAdornment position="end">
                                                <InfoSolidError />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                                <CurrencyInput
                                    sx={{ width: '50%' }}
                                    value={parseCurrency(`${field.amount}`)}
                                    onChange={(value) => {
                                        handleDynamicLegalValueChange(
                                            value,
                                            'amount',
                                            index,
                                            field.quoteTemplateDynamicPriceId,
                                        );
                                        resetStep3Errors();
                                        setErrors({
                                            missingValue: null,
                                            errorDetails: [],
                                        });
                                    }}
                                    allowDecimal
                                    label="Amount"
                                    error={hasError(
                                        'amount',
                                        index,
                                        dynamicFeesErrors.errorDetails,
                                    )}
                                    showErrorAdornment={true}
                                />
                                <IconButton
                                    aria-label="remove-dynamic-field"
                                    onClick={() =>
                                        setDeleteConfirmAlert({
                                            isOpen: true,
                                            index: index,
                                            dynamicFieldId: field.quoteTemplateDynamicPriceId,
                                        })
                                    }
                                >
                                    <RemoveCircleOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddOutlined />}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                            onClick={handleAddNewDynamicLegalFee}
                            disabled={isAddDisabled}
                        >
                            Add fee
                        </Button>
                    </>
                )}
            </Box>
            <AlertDialog
                open={deleteConfirmAlert.isOpen}
                title="Are you sure ?"
                message={`Clicking on 'Accept' will delete the selected add-on field permanently`}
                handleClose={() =>
                    setDeleteConfirmAlert({
                        isOpen: false,
                        index: -1,
                        dynamicFieldId: undefined,
                    })
                }
                handleConfirm={() => {
                    removeDynamicLegalFee(
                        deleteConfirmAlert.index,
                        deleteConfirmAlert.dynamicFieldId,
                    );
                }}
            />
        </Box>
    );
}
